import type { Task, TaskStatus, TaskUrgency } from '@obras/shared';
import { addDays, TASK_STATUS_LABELS } from '@obras/shared';
import {
  ArrowDown,
  Circle,
  CircleCheck,
  CircleDot,
  CirclePause,
  Flag,
  type LucideIcon,
} from 'lucide-react';

import { normalizarBusca } from '../../lib/texto.js';

/*
 * A aparência das tarefas: o ícone e a cor de cada situação, o que a
 * prioridade mostra e como o prazo se lê. Uma tabela só, para a lista, o menu
 * da linha e o modal dizerem a mesma coisa do mesmo jeito.
 */

export const APARENCIA_DA_SITUACAO: Record<
  TaskStatus,
  { readonly icone: LucideIcon; readonly cor: string; readonly barra: string }
> = {
  a_fazer: { icone: Circle, cor: 'text-ink-3', barra: 'bg-ink-3' },
  em_andamento: { icone: CircleDot, cor: 'text-info', barra: 'bg-info' },
  aguardando: { icone: CirclePause, cor: 'text-warning', barra: 'bg-warning' },
  concluida: { icone: CircleCheck, cor: 'text-success', barra: 'bg-success' },
};

/**
 * A ordem das seções da lista: o que está sendo feito, o que vem a seguir, o
 * que espera alguém de fora, e por último o que acabou.
 */
export const ORDEM_DAS_SECOES: readonly TaskStatus[] = [
  'em_andamento',
  'a_fazer',
  'aguardando',
  'concluida',
];

/** O título da seção, no plural quando a situação concorda com "tarefas". */
export const TITULO_DA_SECAO: Record<TaskStatus, string> = {
  em_andamento: TASK_STATUS_LABELS.em_andamento,
  a_fazer: TASK_STATUS_LABELS.a_fazer,
  aguardando: TASK_STATUS_LABELS.aguardando,
  concluida: 'Concluídas',
};

/** A prioridade só aparece quando foge do normal — o normal é o silêncio. */
export const APARENCIA_DA_PRIORIDADE: Partial<
  Record<TaskUrgency, { readonly icone: LucideIcon; readonly cor: string }>
> = {
  urgente: { icone: Flag, cor: 'text-danger' },
  alta: { icone: Flag, cor: 'text-warning' },
  baixa: { icone: ArrowDown, cor: 'text-ink-3' },
};

/** Peso da prioridade na ordem da lista: urgente primeiro. */
const PESO_DA_PRIORIDADE: Record<TaskUrgency, number> = {
  urgente: 0,
  alta: 1,
  normal: 2,
  baixa: 3,
};

export type TomDoPrazo = 'vencido' | 'proximo' | 'normal';

/**
 * O prazo em palavras curtas — "hoje", "amanhã", "ontem", "30/10" — e o tom:
 * vencido (vermelho) e para hoje ou amanhã (âmbar). Tarefa concluída não tem
 * prazo a cumprir: o tom é o normal, sempre.
 */
export function lerPrazo(
  dueAt: string,
  hoje: string,
  concluida: boolean,
): { readonly texto: string; readonly tom: TomDoPrazo; readonly vencido: boolean } {
  const amanha = addDays(hoje, 1);
  const ontem = addDays(hoje, -1);
  const [ano, mes, dia] = dueAt.split('-');

  const texto =
    dueAt === hoje
      ? 'Hoje'
      : dueAt === amanha
        ? 'Amanhã'
        : dueAt === ontem
          ? 'Ontem'
          : ano === hoje.slice(0, 4)
            ? `${dia}/${mes}`
            : `${dia}/${mes}/${ano?.slice(2)}`;

  const vencido = !concluida && dueAt < hoje;
  const tom: TomDoPrazo = concluida
    ? 'normal'
    : vencido
      ? 'vencido'
      : dueAt <= amanha
        ? 'proximo'
        : 'normal';

  return { texto, tom, vencido };
}

export const COR_DO_PRAZO: Record<TomDoPrazo, string> = {
  vencido: 'text-danger',
  proximo: 'text-warning',
  normal: 'text-ink-3',
};

/**
 * A ordem dentro de cada seção: o prazo mais próximo primeiro (o vencido no
 * alto, o sem prazo no fim), depois a prioridade, depois a mais recente. Na
 * seção das concluídas, a concluída por último vem primeiro.
 */
export function ordenarTarefas(tarefas: readonly Task[], status: TaskStatus): Task[] {
  if (status === 'concluida') {
    return [...tarefas].sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''));
  }

  return [...tarefas].sort((a, b) => {
    if (a.dueAt !== b.dueAt) {
      if (a.dueAt === null) return 1;
      if (b.dueAt === null) return -1;
      return a.dueAt.localeCompare(b.dueAt);
    }
    return (
      PESO_DA_PRIORIDADE[a.urgency] - PESO_DA_PRIORIDADE[b.urgency] ||
      b.createdAt.localeCompare(a.createdAt)
    );
  });
}

/** Onde a busca procura: o título, a descrição, os itens e as pessoas. */
export function textoDeBusca(tarefa: Task): string {
  return normalizarBusca(
    [
      tarefa.title,
      tarefa.description ?? '',
      ...tarefa.checklist.map((item) => item.content),
      ...tarefa.members.map((pessoa) => pessoa.name),
    ].join(' '),
  );
}
